// Shared client-side token checks.
//
// Nothing here verifies a signature: the server is the only authority on
// whether a token is valid. The single job of this file is to avoid treating an
// already-expired token as a live session, which otherwise renders the whole
// app for a user whose every API call comes back 401.

/**
 * Decode a JWT payload, or null if it cannot be read.
 *
 * Two details that a naive `atob(part)` gets wrong:
 *
 * 1. JWT uses base64url AND strips the `=` padding. Chrome's atob happens to
 *    accept unpadded input, so this looked fine in testing, but that leniency
 *    is not something to rely on across engines. Padding is restored here.
 * 2. atob returns a binary string, not UTF-8. A payload carrying a non-ASCII
 *    name or address would decode to mojibake and fail JSON.parse.
 */
function readClaims(token) {
  try {
    const part = token.split('.')[1];
    if (!part) return null;

    let b64 = part.replace(/-/g, '+').replace(/_/g, '/');
    while (b64.length % 4) b64 += '=';

    const binary = atob(b64);
    const utf8 = decodeURIComponent(
      Array.from(binary, (ch) => '%' + ch.charCodeAt(0).toString(16).padStart(2, '0')).join('')
    );
    return JSON.parse(utf8);
  } catch {
    return null;
  }
}

/**
 * True only when the token says, in its own `exp` claim, that it has expired.
 *
 * Deliberately fails OPEN. An unreadable token, a missing `exp`, a browser
 * whose atob disagrees with ours: all of those return false, meaning "not known
 * to be expired", and the user is left signed in for the server to judge.
 *
 * The asymmetry is the point. Guessing "expired" when we cannot tell destroys a
 * live session and forces a password re-entry, and because the caller clears
 * storage on the way out it does so permanently. Guessing "still valid" costs
 * at worst one 401 from an API the server was going to reject anyway.
 */
export function isTokenExpired(token) {
  if (!token) return true;
  const claims = readClaims(token);
  if (!claims || typeof claims.exp !== 'number') return false;
  return claims.exp * 1000 <= Date.now();
}

/** The stored token if it is not known to be expired, otherwise null. */
export function getLiveToken(key) {
  try {
    const token = localStorage.getItem(key);
    return isTokenExpired(token) ? null : token;
  } catch {
    // Storage unavailable (private mode, blocked cookies). Treat as signed out
    // rather than crashing the route.
    return null;
  }
}
